import {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { keepPreviousData } from "@tanstack/react-query";
import { TicketingFormCategory, Utils, PublicRoutes, ProductBidStatus } from "@simplyk/common";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/router";

import { AmplitudeEvents } from "../constants/amplitude";
import { getCookieByName } from "../helpers/cookie";
import { trpc } from "../helpers/trpc";
import { useAmplitude } from "../hooks/amplitude/useAmplitude";
import { useAuctionBidsSocket } from "../hooks/useAuctionBidsSocket";

import { FrontendFormContext } from "./FrontendFormContext";

import { TicketingFrontendPageOutput } from "@/common/types/trpc";

export type HighestProductBidsPerRateObject = {
  rateId: string;
  amount?: number | null;
  topBidderName?: string | null;
  topBidderCommandId?: string | null;
  isHotItem: boolean;
  effectiveEndUtc?: string | null;
  isExtended: boolean;
};

export interface FrontendAuctionContextValues {
  highestBidsMapByRateId:
    | {
        [k: string]: HighestProductBidsPerRateObject;
      }
    | undefined;
  commandBidsRateIdsMap:
    | Record<
        string,
        {
          rateId: string;
          amount: number;
          status: ProductBidStatus;
        }
      >
    | null
    | undefined;
  activeAutoBidRateIds: string[];
  activeAutoBidMaxByRateId: Record<string, number>;
  hasAlreadyBid: boolean;
  hasStytchSession: boolean;
  isReturningBidder: boolean;
  existingCommand:
    | { id: string; email: string | null; firstName: string | null; shippingFeeAmount: number | null }
    | undefined;
  bidderCommandIds: string[];
  refreshAuctionData: () => Promise<void>;
  areHighestBidsLoading: boolean;
  isLoading: boolean;
}

const defaultValues: FrontendAuctionContextValues = {
  highestBidsMapByRateId: {},
  commandBidsRateIdsMap: {},
  activeAutoBidRateIds: [],
  activeAutoBidMaxByRateId: {},
  hasAlreadyBid: false,
  hasStytchSession: false,
  isReturningBidder: false,
  existingCommand: undefined,
  bidderCommandIds: [],
  refreshAuctionData: async () => {},
  areHighestBidsLoading: false,
  isLoading: true,
};

interface TicketingProviderProps {
  ticketing: TicketingFrontendPageOutput;
}

export const FrontendAuctionContext = createContext<FrontendAuctionContextValues>(defaultValues);

export const useFrontendAuctionContext = () => useContext(FrontendAuctionContext);

const FrontendAuctionInnerProvider: FunctionComponent<PropsWithChildren<TicketingProviderProps>> = ({
  children,
  ticketing,
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { logAmplitudeEvent } = useAmplitude();
  const { setSelectedPaymentMethod } = useContext(FrontendFormContext);

  const [auctionBidsVersion, setAuctionBidsVersion] = useState<string | undefined>(undefined);

  const isGetHighestBidsPerRateEnabled = Boolean(ticketing.formCategory === TicketingFormCategory.Auction);

  useAuctionBidsSocket({
    ticketingId: isGetHighestBidsPerRateEnabled ? ticketing.id : null,
    onUpdated: setAuctionBidsVersion,
  });

  const {
    data: highestBidsPerRate,
    isLoading: areHighestBidsLoading,
    // Use refetchHighestBidsPerRateCallback instead
    refetch: refetchHighestBidsPerRate,
  } = trpc.form_getHighestBidsPerRate.useQuery(
    { ticketingId: ticketing.id, version: auctionBidsVersion },
    {
      enabled: isGetHighestBidsPerRateEnabled,
      placeholderData: keepPreviousData,
    }
  );

  const refetchHighestBidsPerRateCallback = useCallback(() => {
    if (isGetHighestBidsPerRateEnabled) {
      void refetchHighestBidsPerRate();
    }
  }, [refetchHighestBidsPerRate, isGetHighestBidsPerRateEnabled]);

  const highestBidsMapByRateId = useMemo(() => {
    return highestBidsPerRate && Utils.arrayToKeyMap(highestBidsPerRate, (bid) => bid.rateId);
  }, [highestBidsPerRate]);

  const bidderToken = (searchParams.get("bidderToken") || undefined) as string | undefined;
  // oxlint-disable-next-line no-console
  console.log("bidderToken", bidderToken, searchParams);
  const isAuctionForm = ticketing.formCategory === TicketingFormCategory.Auction;

  const hasStytchSessionCookie = Boolean(getCookieByName("hasStytchSession"));
  const isGetCommandBidsEnabled = isAuctionForm;

  const {
    data: commandBidsData,
    error,
    refetch: refetchCommandBids,
  } = trpc.form_getCommandBids.useQuery(
    { bidderToken, ticketingId: ticketing.id, version: auctionBidsVersion },
    { enabled: isGetCommandBidsEnabled, placeholderData: keepPreviousData }
  );
  const refetchCommandBidsCallback = useCallback(() => {
    if (isGetCommandBidsEnabled) {
      void refetchCommandBids();
    }
  }, [refetchCommandBids, isGetCommandBidsEnabled]);

  useEffect(() => {
    if (bidderToken) {
      logAmplitudeEvent(AmplitudeEvents.TrackBidsClicked, { from: "ConfirmationEmail" });
    }
  }, [logAmplitudeEvent, bidderToken]);

  useEffect(() => {
    if (hasStytchSessionCookie) {
      return;
    }
    if (error?.shape?.data?.code === "bidder_token_expired" || error?.shape?.data?.code === "invalid_bidder_token") {
      void router.push({ pathname: PublicRoutes.ExpiredBidderLink, query: { backUrl: router.asPath } });
    }
  }, [error?.shape?.data?.code, router, hasStytchSessionCookie]);

  const refreshAuctionData = useCallback(async () => {
    await Promise.all([refetchHighestBidsPerRateCallback(), refetchCommandBidsCallback()]);
  }, [refetchCommandBidsCallback, refetchHighestBidsPerRateCallback]);

  const commandBidsRateIdsMap = useMemo(
    () => commandBidsData && Utils.arrayToKeyMap(commandBidsData.productBids, (item) => item.rateId),
    [commandBidsData]
  );
  const activeAutoBidRateIds = commandBidsData?.activeAutoBidRateIds ?? [];
  const activeAutoBidMaxByRateId = commandBidsData?.activeAutoBidMaxByRateId ?? {};
  const hasAlreadyBid = Boolean(commandBidsData && commandBidsData.productBids.length > 0);
  const bidPaymentMethod = commandBidsData?.command.paymentMethod;

  useEffect(() => {
    if (bidPaymentMethod) {
      setSelectedPaymentMethod(bidPaymentMethod, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidPaymentMethod]);

  const existingCommand = commandBidsData?.command;
  const bidderCommandIds = useMemo(() => commandBidsData?.commandIds ?? [], [commandBidsData?.commandIds]);
  const isReturningBidder = Boolean(existingCommand && hasAlreadyBid && existingCommand.firstName);
  const isLoading = !commandBidsData;

  return (
    <FrontendAuctionContext.Provider
      value={{
        highestBidsMapByRateId,
        commandBidsRateIdsMap,
        activeAutoBidRateIds,
        activeAutoBidMaxByRateId,
        hasAlreadyBid,
        hasStytchSession: hasStytchSessionCookie,
        isReturningBidder,
        existingCommand,
        bidderCommandIds,
        refreshAuctionData,
        areHighestBidsLoading,
        isLoading,
      }}
    >
      {children}
    </FrontendAuctionContext.Provider>
  );
};

export const FrontendAuctionProvider: FunctionComponent<PropsWithChildren<TicketingProviderProps>> = (props) => {
  if (props.ticketing.formCategory !== TicketingFormCategory.Auction) {
    return <>{props.children}</>;
  }

  return <FrontendAuctionInnerProvider {...props} />;
};
